import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import Header from '@/components/layout/Header';
import Footer from '@/components/layout/Footer';
import SEO from '@/components/SEO';
import SeoBreadcrumbs from '@/components/SeoBreadcrumbs';
import { ArrowRight, CheckCircle, Clock, Home, Users, FileText } from 'lucide-react';

const LandlordFloorPlans = () => {
  const breadcrumbs = [
    { name: "Home", path: "/" },
    { name: "Landlord Floor Plans", path: "/landlord-floor-plans" }
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEO 
        pageId="landlord-floor-plans"
        defaultTitle="Landlord Floor Plans UK | House Plan Direct"
        defaultDescription="Professional floor plans for landlords and rental properties. Attract quality tenants with clear property layouts. From £40, 24-48 hour turnaround."
        aiSummary="House Plan Direct provides floor plans for landlords and rental properties across the UK. Our floor plans help landlords attract quality tenants by clearly showing property layouts. Perfect for Rightmove, Zoopla, OpenRent, and letting agent marketing. Plans from £40."
        keywords="landlord floor plans, rental property floor plan, floor plan for rental, letting agent floor plans, rental listing floor plan UK"
      />
      <SeoBreadcrumbs items={breadcrumbs} />
      <Header />
      
      {/* Hero Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-purple-50 to-white">
        <div className="max-w-4xl mx-auto text-center">
          <div className="flex items-center justify-center mb-4">
            <Home className="h-12 w-12 text-purple-600" />
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-6">
            Landlord Floor Plans
          </h1>
          <p className="text-xl text-gray-600 mb-8">
            Professional floor plans for rental properties. Attract quality tenants with clear layouts.
          </p>
          <div className="flex flex-wrap justify-center gap-4 mb-8">
            <span className="flex items-center bg-white px-4 py-2 rounded-full shadow-sm text-sm">
              <CheckCircle className="h-4 w-4 text-green-600 mr-2" /> From £40
            </span>
            <span className="flex items-center bg-white px-4 py-2 rounded-full shadow-sm text-sm">
              <Clock className="h-4 w-4 text-blue-600 mr-2" /> 24-48 hour delivery
            </span>
            <span className="flex items-center bg-white px-4 py-2 rounded-full shadow-sm text-sm">
              <Users className="h-4 w-4 text-purple-600 mr-2" /> Attract better tenants
            </span>
          </div>
          <Link to="/order">
            <Button size="lg" className="bg-purple-600 hover:bg-purple-700">
              Order Floor Plan <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
          <p className="text-sm text-gray-500 mt-4">
            Prefer to do it yourself? Use the free <Link to="/floor-plan-creator" className="text-purple-600 underline hover:text-purple-700" data-testid="landlord-link-floor-plan-creator">Floor Plan Creator</Link>.
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Floor Plans for Rental Properties</h2>
          <p className="text-gray-600 mb-8">
            As a landlord, attracting quality tenants quickly is essential. Floor plans help potential tenants 
            understand your property's layout before viewing, saving you time and attracting serious applicants 
            who are a better fit for your property.
          </p>

          <div className="grid md:grid-cols-2 gap-8 mb-12">
            <div className="bg-purple-50 p-6 rounded-xl">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Benefits for Landlords</h3>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-purple-600 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-600">Attract quality tenants faster</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-purple-600 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-600">Reduce void periods</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-purple-600 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-600">Professional listing presentation</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-purple-600 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-600">Useful for multiple listings</span>
                </li>
              </ul>
            </div>
            <div className="bg-gray-50 p-6 rounded-xl">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Perfect For</h3>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-green-600 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-600">Rightmove & Zoopla listings</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-green-600 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-600">OpenRent & SpareRoom ads</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-green-600 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-600">Letting agent marketing</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-green-600 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-600">Tenant information packs</span>
                </li>
              </ul>
            </div>
          </div>

          {/* CTA */}
          <div className="bg-purple-600 text-white rounded-xl p-8 text-center">
            <h3 className="text-2xl font-bold mb-4">Get Your Rental Property Floor Plan</h3>
            <p className="text-purple-100 mb-6">Upload your sketch and receive a professional floor plan within 24-48 hours.</p>
            <Link to="/order">
              <Button size="lg" variant="secondary" className="bg-white text-purple-600 hover:bg-gray-100">
                Order Now <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default LandlordFloorPlans;
